import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import Avatar from '../../../components/ui/Avatar'
import EmptyState from '../../../components/ui/EmptyState'
import SectionCard from '../../../components/ui/SectionCard'
import type { DelegateePerformance } from '../types'

interface Props {
  rows: DelegateePerformance[]
  /** Kartın altındaki "Tümünü Gör" bağlantısı (Raporum revizyonu §6.4). */
  footer?: ReactNode
}

/**
 * "En Fazla Aksiyon Atadığım Yöneticiler" (068 — analiz §2.9/§2.10, spec §K9; 2026-08-19 revizyonu §6).
 * Her satır: sıra, rozet + ad + birim, atadığım adet ve **tamamlanma oranı**.
 *
 * <p><b>2026-08-19'da yığılmış çubuk yerini tamamlanma oranı çubuğuna bıraktı.</b> Analiz §6.4 kolonu
 * açıkça tanımlıyor: "Tamamlanma oranı hem progress bar hem de yüzde değeri olarak gösterilecektir.
 * Progress bar yeşil renkte olacaktır." Önceki çubuk iki segmentli (tamamlanan + devam eden) ve satırlar
 * arası **ortak ölçekliydi** — yani uzunluğu "kaç iş" demekti, oranı değil; oranı okumak için yanındaki
 * yüzdeye bakmak gerekiyordu. Yeni çubuk <b>0–100</b> aralığında kendi oranını çizer ve iki satır
 * doğrudan karşılaştırılabilir. Adet bilgisi kaybolmaz: kendi kolonunda durur.
 *
 * <p>Küme "Devrettiklerim" ile aynıdır (üstteki "Atadığım Aksiyonların Durumu" bölümüyle <b>aynı</b>
 * kaynak) → iki bölüm birbiriyle çelişemez. Kalkmış İptal statüsünün kayıtları hiçbir sayaca girmez, bu
 * yüzden `biten + devam eden = atadığım` ve oran çubukla tutarlıdır.
 *
 * <p>Renk tek bilgi taşıyıcısı değildir: oran çubuğun yanında <b>metin</b> olarak da yazar ve çubuk
 * `role="img"` + erişilebilir ad taşır. Kolon başlıkları mockup'takilerle birebir aynıdır (tablo gibi
 * okunur ama gerçek bir tablo değildir — satır sayısı sabit ≤5 ve hücrelerde işlem yoktur).
 */
export default function TopDelegateesCard({ rows, footer }: Readonly<Props>) {
  const { t } = useTranslation()
  const title = t('home.my.delegatees.title')

  if (rows.length === 0) {
    return (
      <SectionCard title={title}>
        <EmptyState
          icon="pi pi-users"
          title={t('home.my.delegatees.emptyTitle')}
          description={t('home.my.delegatees.emptyDesc')}
        />
      </SectionCard>
    )
  }

  return (
    <SectionCard
      title={title}
      hint={t('home.my.delegatees.hint')}
      footer={footer}
      className="home-delegatees"
    >
      <div className="home-delegatees-head" aria-hidden="true">
        <span className="home-delegatees-rank">#</span>
        <span>{t('home.my.delegatees.columns.manager')}</span>
        <span className="home-delegatees-count">{t('home.my.delegatees.columns.assigned')}</span>
        <span className="home-delegatees-rate-head">
          {t('home.my.delegatees.columns.completionRate')}
        </span>
      </div>
      <ol className="home-delegatees-list">
        {rows.map((r) => {
          const percent = r.completionRate
          return (
            <li key={r.employeeId} className="home-delegatees-row">
              {/* Rozet rengi SABİT (`--avatar-bg`, 2026-08-27): eskiden sicilden türetiliyordu.
                  Ayırt etme işini yanındaki ad yapıyor, renk değil. Sıra numarası yerine geçer
                  (solda, dikey ortalı). */}
              <Avatar name={r.fullName ?? ''} className="home-delegatees-avatar" />
              <div className="home-delegatees-body">
                {/* Ad ve adet/oran AYNI satırda başlar; yer daralınca adet/oran KENDİ satırına
                    sarar (ad tek satırda kırpılı kalır — sarması gereken adet/orandır, ad değil). */}
                <div className="home-delegatees-top">
                  <span className="home-delegatees-ident">
                    <span className="home-delegatees-name">{r.fullName}</span>
                    {r.jobTitle && <span className="home-delegatees-title">{r.jobTitle}</span>}
                  </span>
                  {/* `data-unit` yalnız telefon genişliğinde `::after` ile görünür (home.css) —
                      masaüstünde kolon başlığı zaten "Atadığım Görev Sayısı" der, tekrar etmez. */}
                  <span className="home-delegatees-meta">
                    <span className="home-delegatees-count" data-unit={t('home.my.delegatees.unit')}>
                      {r.assignedCount}
                    </span>
                    {/* Oran ondalıklı gelir (ör. 71.4) — görünen ayraç dile göre biçimlenir. */}
                    <span className="home-delegatees-rate">%{percent.toLocaleString()}</span>
                  </span>
                </div>
                <span
                  className="home-delegatees-progress"
                  role="img"
                  aria-label={t('home.my.delegatees.rateLabel', {
                    name: r.fullName ?? '',
                    percent,
                    completed: r.completedCount,
                    assigned: r.assignedCount,
                  })}
                >
                  {/* Genişlik ORANDIR (0–100), adet değil → iki satır doğrudan karşılaştırılabilir. */}
                  <i style={{ width: `${Math.min(100, Math.max(0, percent))}%` }} />
                </span>
              </div>
            </li>
          )
        })}
      </ol>
    </SectionCard>
  )
}
